import lodashCompact from 'lodash/fp/compact';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  createFormFieldsByIdGetter,
  createFormValidationFn,
  createInitialValues,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrCard, {
  SR_CARD_BULLET_COLOR_YELLOW,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrFooterSaveButton from 'client/components/SrFooterSaveButton';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const SR_ENABLE_AUTO_CHARGE_UPDATES = false;

const FORM_TYPE_LISTING = 'listing';
const FORM_TYPE_PARTNER = 'partner';

const FORM_TYPES = [FORM_TYPE_LISTING, FORM_TYPE_PARTNER];

const getFormFields = ({ formType, leasingOperationsManagers, accountManagers, values }) => {
  return [
    showFieldIf(formType === FORM_TYPE_PARTNER, {
      id: 'leasingOperationsManagerUserId',
      title: 'Lease Operation Manager (Required)',
      type: 'select',
      properties: {
        choices: lodashCompact([
          ...(leasingOperationsManagers || []).map((lm) => ({
            label: lm.name,
            value: lm.id,
          })),
        ]),
      },
      validations: ['required'],
    }),
    showFieldIf(formType === FORM_TYPE_PARTNER, {
      id: 'accountManagerUserId',
      title: 'Account Manager',
      type: 'select',
      properties: {
        choices: lodashCompact([
          ...(accountManagers || []).map((am) => ({
            label: am.name,
            value: am.id,
          })),
        ]),
      },
      validations: ['differentValue:leasingOperationsManagerUserId'],
    }),
    {
      id: 'highPriority',
      title: 'High Priority',
      subtitle:
        formType === FORM_TYPE_PARTNER
          ? 'Marking the partner a high priority will elevate their leads to the top of the Call Hitlist.'
          : 'Marking the listing high priority will display its leads in the Call Hitlist',
      type: 'yes_no',
      validations: ['required'],
    },
    {
      id: 'leaseFeePercentage',
      type: 'percentage',
      title: 'Lease Fee (%)',
      validations: ['required'],
    },
    {
      id: 'leaseFeeMinAmount',
      type: 'currency',
      title: 'Lease Fee Minimum',
      validations: ['required'],
    },
    {
      id: 'leaseFeeMaxAmount',
      type: 'currency',
      title: 'Lease Fee Maximum',
      validations: values.leaseFeeMinAmount ? ['greaterOrEqualTo'] : [],
      properties: {
        threshold: values.leaseFeeMinAmount || 0,
      },
    },
    {
      id: 'leaseFeeThirdPartyAccepted',
      type: 'yes_no',
      title: 'Partner agrees to pay Sunroom for 3rd party agents',
      validations: ['required'],
    },
    showFieldIf(values.leaseFeeThirdPartyAccepted, {
      id: 'leaseFeeThirdPartyPercentage',
      type: 'percentage',
      title: 'Lease w/3rd Party Agent (%)',
      validations: ['required'],
    }),
    {
      id: 'withdrawalFee',
      type: 'currency',
      title: 'Withdraw Fee',
      validations: ['required'],
    },
    {
      id: 'failedSetupFee',
      type: 'currency',
      title: 'Failed Setup Fee',
      validations: ['required'],
    },
    {
      id: 'renewalFeePercentage',
      type: 'percentage',
      title: 'Renewal Fee (%)',
      validations: ['required'],
    },
    {
      id: 'renewalFeeMinAmount',
      type: 'currency',
      title: 'Renewal Fee Minimum',
      validations: values.renewalFeeMinAmount ? ['positiveNumber'] : [],
    },
    {
      id: 'virtualTourFee',
      type: 'currency',
      title: '3D Tour Fee',
      validations: ['required'],
    },
    {
      id: 'discountLongDOM',
      type: 'yes_no',
      title: 'Discount Lease Fee for Long DOM',
      validations: ['required'],
    },
    showFieldIf(values?.discountLongDOM, {
      id: 'longDOMThreshold',
      type: 'number',
      title: 'Long DOM (in days)',
      validations: ['required'],
    }),
    showFieldIf(values?.discountLongDOM, {
      id: 'longDOMLeaseFeePercentage',
      type: 'percentage',
      title: 'Long DOM Lease Fee (%)',
      validations: ['required'],
    }),
    showFieldIf(formType === FORM_TYPE_PARTNER && SR_ENABLE_AUTO_CHARGE_UPDATES, {
      id: 'autoChargeEnabled',
      type: 'yes_no',
      title: 'AutoCharge',
      subtitle: '(for legacy billing, choose "no")',
      validations: ['required'],
    }),
    {
      id: 'partnerTaskCutLawnEnabled',
      type: 'yes_no',
      title: 'Scheduled lawn cuts',
    },
    {
      id: 'partnerTaskCleanEnabled',
      type: 'yes_no',
      title: 'Scheduled cleanings',
    },
    showFieldIf(formType === FORM_TYPE_PARTNER, {
      id: 'showLeadChatConversations',
      type: 'yes_no',
      title: 'Lead chat conversations are visible',
      validations: ['required'],
    }),
  ];
};

const SrPartnerSettingsAdminForm = ({
  partner,
  listing,
  billingFees,
  formType,
  leasingOperationsManagers,
  accountManagers,
  onSubmit,
  inProgress,
}) => {
  const [shouldShowSavedModal, setShouldShowSavedModal] = useState(false);

  const _getDefaultValues = () => {
    if (formType === FORM_TYPE_LISTING) {
      return {
        ...billingFees,
        highPriority: listing.highPriority,
        partnerTaskCutLawnEnabled: listing.partnerTaskCutLawnEnabled,
        partnerTaskCleanEnabled: listing.partnerTaskCleanEnabled,
      };
    }

    return {
      leasingOperationsManagerUserId: partner.leasingOperationsManagerUserId,
      accountManagerUserId: partner.accountManagerUserId || accountManagers[0].id,
      autoChargeEnabled: partner.experienceConfiguration?.autoChargeEnabled,
      showLeadChatConversations: partner.experienceConfiguration?.showLeadChatConversations,
      partnerTaskCutLawnEnabled: partner.experienceConfiguration?.partnerTaskCutLawnEnabled,
      partnerTaskCleanEnabled: partner.experienceConfiguration?.partnerTaskCleanEnabled,
      highPriority: partner.highPriority,
      ...billingFees,
    };
  };

  const defaultFormValues = _getDefaultValues();

  const _handleSubmit = (values, formikBag) => {
    onSubmit(values, formikBag);
    setShouldShowSavedModal(true);

    setTimeout(() => {
      setShouldShowSavedModal(false);
    }, 1000);
  };

  return (
    <>
      <Formik
        onSubmit={_handleSubmit}
        initialValues={createInitialValues({
          formFields: getFormFields({
            formType,
            leasingOperationsManagers,
            accountManagers,
            values: defaultFormValues,
          }),
          defaultValues: defaultFormValues,
        })}
        validate={(values) => {
          const formFields = getFormFields({
            formType,
            leasingOperationsManagers,
            accountManagers,
            values: values,
          });
          return createFormValidationFn(formFields)(values);
        }}
        render={(formikBag) => {
          const formFields = getFormFields({
            formType,
            leasingOperationsManagers,
            accountManagers,
            values: formikBag.values,
          });
          const getFormFieldsById = createFormFieldsByIdGetter(formFields);

          return (
            <>
              <Form>
                {formType === FORM_TYPE_PARTNER && (
                  <SrCard
                    color={SR_CARD_BULLET_COLOR_YELLOW}
                    contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                    header="Assign Managers"
                    subheader={
                      'Here you can assign the Leasing Operations Manager, ' +
                      'and the Account Manager for this partner. ' +
                      'The Account Manager is optional.'
                    }
                  >
                    <SrGridRow columns={2}>
                      <SrFormikField
                        formField={getFormFieldsById('leasingOperationsManagerUserId')}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                      <SrFormikField
                        formField={getFormFieldsById('accountManagerUserId')}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    </SrGridRow>
                    <SrFormikField
                      formField={getFormFieldsById('highPriority')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrCard>
                )}
                <SrCard
                  color={SR_CARD_BULLET_COLOR_YELLOW}
                  contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                  header="Billing Fees"
                  subheader={
                    FORM_TYPE_PARTNER
                      ? 'The changes made on these partner-level settings will affect all current ' +
                        'listings, and newly created listings. If the fees are changed at the listing ' +
                        'level, they will override these partner-level settings.'
                      : 'The changes made on these settings will only affect this listing, and will ' +
                        'override any differences in the partner-level settings.'
                  }
                  paddingRight="8px"
                >
                  <SrGridRow autofit autofitMinColumnWidth="185px">
                    <SrFormikField
                      formField={getFormFieldsById('leaseFeePercentage')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                    <SrFormikField
                      formField={getFormFieldsById('leaseFeeMinAmount')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                    <SrFormikField
                      formField={getFormFieldsById('leaseFeeMaxAmount')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrGridRow>
                  <SrGridRow columns={2} columnGap="8px" columnWrapMobile columnWrapTablet>
                    <SrFormikField
                      formField={getFormFieldsById('leaseFeeThirdPartyAccepted')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                    <SrBox maxWidth="185px">
                      <SrFormikField
                        formField={getFormFieldsById('leaseFeeThirdPartyPercentage')}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    </SrBox>
                  </SrGridRow>
                  <SrGridRow autofit autofitMinColumnWidth="185px">
                    <SrFormikField
                      formField={getFormFieldsById('withdrawalFee')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrGridRow>
                  <SrGridRow autofit autofitMinColumnWidth="185px">
                    <SrFormikField
                      formField={getFormFieldsById('failedSetupFee')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrGridRow>
                  <SrGridRow autofit autofitMinColumnWidth="185px">
                    <SrFormikField
                      formField={getFormFieldsById('virtualTourFee')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrGridRow>
                  <SrGridRow autofit autofitMinColumnWidth="185px">
                    <SrFormikField
                      formField={getFormFieldsById('renewalFeePercentage')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                    <SrFormikField
                      formField={getFormFieldsById('renewalFeeMinAmount')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrGridRow>
                  <SrGridRow columns={3} columnGap="8px" columnWrapMobile columnWrapTablet>
                    <SrFormikField
                      formField={getFormFieldsById('discountLongDOM')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                    {formikBag.values.discountLongDOM && (
                      <>
                        <SrFormikField
                          formField={getFormFieldsById('longDOMThreshold')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrFormikField
                          formField={getFormFieldsById('longDOMLeaseFeePercentage')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </>
                    )}
                  </SrGridRow>
                </SrCard>
                {formType === FORM_TYPE_PARTNER && SR_ENABLE_AUTO_CHARGE_UPDATES && (
                  <SrCard
                    color={SR_CARD_BULLET_COLOR_YELLOW}
                    contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                    header="Billing Rules"
                  >
                    <SrGridRow>
                      <SrFormikField
                        formField={getFormFieldsById('autoChargeEnabled')}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    </SrGridRow>
                  </SrCard>
                )}
                {formType === FORM_TYPE_LISTING && (
                  <SrCard
                    color={SR_CARD_BULLET_COLOR_YELLOW}
                    contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                    header="Listing Settings"
                  >
                    <SrGridRow>
                      <SrFormikField
                        formField={getFormFieldsById('highPriority')}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    </SrGridRow>
                  </SrCard>
                )}

                <SrCard
                  color={SR_CARD_BULLET_COLOR_YELLOW}
                  contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                  header="Task List"
                >
                  <SrGridRow>
                    <SrFormikField
                      formField={getFormFieldsById('partnerTaskCutLawnEnabled')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                    <SrFormikField
                      formField={getFormFieldsById('partnerTaskCleanEnabled')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  </SrGridRow>
                </SrCard>
                {formType === FORM_TYPE_PARTNER && (
                  <SrCard
                    color={SR_CARD_BULLET_COLOR_YELLOW}
                    contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                    header="Listing Page Features"
                  >
                    <SrGridRow>
                      <SrFormikField
                        formField={getFormFieldsById('showLeadChatConversations')}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    </SrGridRow>
                  </SrCard>
                )}
                <SrStaticFooter>
                  <span />
                  <SrFooterSaveButton isSubmitting={inProgress} />
                </SrStaticFooter>
              </Form>
            </>
          );
        }}
      />
      {shouldShowSavedModal && (
        <SrSimpleMessageModal
          iconUrl="/images/checkmark-circle-large-green.svg"
          headline="Saved!"
          maxWidth={400}
          minWidth={400}
          theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
        />
      )}
    </>
  );
};

SrPartnerSettingsAdminForm.propTypes = {
  partner: PropTypes.object.isRequired,
  billingFees: PropTypes.object.isRequired,
  formType: PropTypes.oneOf(FORM_TYPES).isRequired,
  leasingOperationsManagers: PropTypes.array,
  accountManagers: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
  inProgress: PropTypes.bool.isRequired,
};

SrPartnerSettingsAdminForm.displayName = 'SrPartnerSettingsAdminForm';

export default SrPartnerSettingsAdminForm;
